import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  LogIn, 
  LayoutDashboard, 
  CheckCircle, 
  Award, 
  HelpCircle,
  Database,
  Terminal,
  Brain,
  Play,
  FileText,
  Clock,
  AlertCircle,
  Target,
  Sparkles,
  GraduationCap,
  Search,
  Heart,
  Compass,
  Download,
  TrendingUp,
  Smartphone
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const StudentHelp = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">Student Help Guide</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about using Neo Prism Academy
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Accordion type="single" collapsible className="space-y-4">
          
          {/* What's New Section - NEW */}
          <AccordionItem value="whats-new" className="border rounded-lg px-4 border-primary/30 bg-primary/5">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold flex items-center gap-2">
                    What's New
                    <Badge variant="default" className="ml-2">Updated</Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground">Latest features and improvements</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  We've added exciting new features to enhance your learning experience!
                </p>
                <div className="grid gap-3">
                  <Card className="bg-card border-primary/20">
                    <CardContent className="p-4 flex items-start gap-3">
                      <GraduationCap className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">My Courses - Your Learning Hub</p>
                        <p className="text-sm text-muted-foreground">
                          Students now land directly on the <Link to="/my-courses" className="text-primary hover:underline">My Courses</Link> page after login. 
                          Continue learning with one click, track your progress, and see all your enrolled courses in one place.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-primary/20">
                    <CardContent className="p-4 flex items-start gap-3">
                      <Search className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Course Search</p>
                        <p className="text-sm text-muted-foreground">
                          Quickly find any course with our powerful <Link to="/search-courses" className="text-primary hover:underline">search feature</Link>. 
                          Filter by category, difficulty, and technology.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-primary/20">
                    <CardContent className="p-4 flex items-start gap-3">
                      <Heart className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Favorites</p>
                        <p className="text-sm text-muted-foreground">
                          Save courses you're interested in to your <Link to="/favorites" className="text-primary hover:underline">Favorites</Link>. 
                          Easily access them later without searching.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-primary/20">
                    <CardContent className="p-4 flex items-start gap-3">
                      <Compass className="w-5 h-5 text-emerald-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Learning Paths</p>
                        <p className="text-sm text-muted-foreground">
                          Follow structured <Link to="/learning-paths" className="text-primary hover:underline">learning paths</Link> designed by experts. 
                          Take skill assessments and get personalized recommendations.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-primary/20">
                    <CardContent className="p-4 flex items-start gap-3">
                      <Smartphone className="w-5 h-5 text-purple-500 mt-0.5" />
                      <div>
                        <p className="font-medium">Mobile App (PWA)</p>
                        <p className="text-sm text-muted-foreground">
                          Install Neo Prism Academy on your device! Visit <Link to="/install" className="text-primary hover:underline">Install App</Link> to 
                          add it to your home screen for a native app experience with offline access.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* My Courses - Your Learning Hub - NEW */}
          <AccordionItem value="my-courses" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <GraduationCap className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">My Courses - Your Learning Hub</h3>
                  <p className="text-sm text-muted-foreground">Your personalized learning dashboard</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  <Link to="/my-courses" className="text-primary hover:underline">My Courses</Link> is your central hub for learning. 
                  Here's what you can do:
                </p>
                <div className="grid gap-3">
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium flex items-center gap-2">
                        <Play className="w-4 h-4 text-primary" /> Continue Learning
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Pick up exactly where you left off with our Continue Learning Hero. 
                        One click takes you to your last accessed module.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-500" /> Progress Tracking
                      </p>
                      <p className="text-sm text-muted-foreground">
                        See your overall progress, courses in progress, completed courses, and completion percentages at a glance.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium flex items-center gap-2">
                        <Award className="w-4 h-4 text-amber-500" /> Course Completion Celebration
                      </p>
                      <p className="text-sm text-muted-foreground">
                        When you complete a course, you'll see a celebration modal with confetti! 
                        Claim your certificate right from the celebration screen.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-purple-500" /> Quick Actions
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Quick access buttons to Browse All Courses, Search, view Certificates, explore Learning Paths, and get Help.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Learning Paths - NEW */}
          <AccordionItem value="learning-paths" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <Compass className="w-5 h-5 text-cyan-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Learning Paths</h3>
                  <p className="text-sm text-muted-foreground">Structured learning journeys</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  <Link to="/learning-paths" className="text-primary hover:underline">Learning Paths</Link> are curated sequences of courses 
                  designed to help you master specific skills or roles.
                </p>
                <div className="grid gap-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">1</Badge>
                    <div>
                      <p className="font-medium">Choose Your Path</p>
                      <p className="text-sm text-muted-foreground">
                        Browse paths like "Full Stack Developer", "Data Scientist", "Cloud Engineer", and more.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">2</Badge>
                    <div>
                      <p className="font-medium">Take Skill Assessments</p>
                      <p className="text-sm text-muted-foreground">
                        Optional assessments help identify your current level and recommend the right starting point.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">3</Badge>
                    <div>
                      <p className="font-medium">Follow the Journey</p>
                      <p className="text-sm text-muted-foreground">
                        Complete courses in the recommended order to build skills progressively.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">4</Badge>
                    <div>
                      <p className="font-medium">Earn Path Completion</p>
                      <p className="text-sm text-muted-foreground">
                        Complete all courses in a path to demonstrate mastery of the entire skill set.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/learning-paths">Explore Learning Paths →</Link>
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Getting Started */}
          <AccordionItem value="getting-started" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <LogIn className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Getting Started</h3>
                  <p className="text-sm text-muted-foreground">Login and account access</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">No Self-Registration</p>
                    <p className="text-sm text-muted-foreground">
                      Student accounts are created and managed by administrators. You cannot sign up on your own.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Login Process</p>
                    <p className="text-sm text-muted-foreground">
                      Go to <Link to="/login" className="text-primary hover:underline">/login</Link> and enter the credentials provided by your administrator. 
                      You'll be taken directly to <Link to="/my-courses" className="text-primary hover:underline">My Courses</Link> after login.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Account Approval</p>
                    <p className="text-sm text-muted-foreground">
                      Some accounts may require administrator approval before you can access courses.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Forgot Password?</p>
                    <p className="text-sm text-muted-foreground">
                      Use the "Forgot password?" link on the login page to reset your password via email.
                    </p>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Dashboard Navigation */}
          <AccordionItem value="dashboard" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <LayoutDashboard className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Your Dashboard</h3>
                  <p className="text-sm text-muted-foreground">Overview and statistics</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  The <Link to="/dashboard" className="text-primary hover:underline">Dashboard</Link> provides an overview of your learning journey.
                </p>
                <div className="grid gap-3">
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">📚 Assigned Courses</p>
                      <p className="text-sm text-muted-foreground">View courses assigned to you by administrators. Only assigned courses are accessible.</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">📊 Progress Tracking</p>
                      <p className="text-sm text-muted-foreground">See your completion percentage, modules finished, and quiz scores.</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">🔥 Learning Streak</p>
                      <p className="text-sm text-muted-foreground">Track your daily learning streak and maintain consistency.</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-muted/30">
                    <CardContent className="p-4">
                      <p className="font-medium">🏆 Certificates Earned</p>
                      <p className="text-sm text-muted-foreground">Quick access to your earned certificates.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Learning a Module */}
          <AccordionItem value="modules" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <BookOpen className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Learning a Module</h3>
                  <p className="text-sm text-muted-foreground">Understanding the 4 module tabs</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Each module contains 4 tabs to guide your learning:
                </p>
                <div className="grid gap-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">1</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Play className="w-4 h-4" /> Learn
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Main content with text, code examples, and audio narration. Audio only plays in this tab.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">2</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Terminal className="w-4 h-4" /> Labs
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Hands-on practice exercises with real code execution.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">3</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> Quiz
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Test your knowledge. Must pass with 80% or higher.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Badge variant="outline" className="mt-0.5">4</Badge>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Resources
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Additional materials, documentation, and references.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border mt-4">
                  <p className="font-medium mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4 text-emerald-500" /> Course Certificates
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Certificates are earned at the <strong>course level</strong> after completing all modules and quizzes. 
                    View your certificate eligibility on the Course Detail page or visit the{' '}
                    <Link to="/certificates" className="text-primary hover:underline">Certificates page</Link>.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Quiz Requirements */}
          <AccordionItem value="quizzes" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Target className="w-5 h-5 text-amber-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Quiz Requirements</h3>
                  <p className="text-sm text-muted-foreground">Passing scores and attempts</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-amber-500/30 bg-amber-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="w-5 h-5 text-amber-500" />
                        Passing Score
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-amber-500">80%</p>
                      <p className="text-sm text-muted-foreground">Minimum score required to pass each quiz</p>
                    </CardContent>
                  </Card>
                  <Card className="border-blue-500/30 bg-blue-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-500" />
                        Maximum Attempts
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-500">3</p>
                      <p className="text-sm text-muted-foreground">Attempts allowed per module quiz</p>
                    </CardContent>
                  </Card>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <p className="font-medium mb-2">💡 Tips for Success</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Review the Learn tab content thoroughly before attempting the quiz</li>
                    <li>• Complete the Labs for hands-on practice</li>
                    <li>• If you don't pass, review the material before your next attempt</li>
                  </ul>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Certificate Requirements */}
          <AccordionItem value="certificates" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Award className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Certificate Requirements</h3>
                  <p className="text-sm text-muted-foreground">How to earn your certificate</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  To earn a certificate for a course, you must meet ALL of the following requirements:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">100% Module Completion</p>
                      <p className="text-sm text-muted-foreground">Complete all modules in the course</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">80%+ on All Quizzes</p>
                      <p className="text-sm text-muted-foreground">Pass each individual module quiz with 80% or higher</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="font-medium">80%+ Course Average</p>
                      <p className="text-sm text-muted-foreground">Maintain an overall course average of 80% or higher</p>
                    </div>
                  </div>
                </div>
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    Once eligible, claim your certificate from:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">Course Detail Page</Badge>
                    <Badge variant="secondary">
                      <Link to="/certificates" className="hover:underline">Certificates page</Link>
                    </Badge>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Special Features */}
          <AccordionItem value="features" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <Brain className="w-5 h-5 text-cyan-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">Special Features</h3>
                  <p className="text-sm text-muted-foreground">Additional learning tools</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="grid gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Brain className="w-5 h-5 text-cyan-500" />
                      AI Tutor
                    </CardTitle>
                    <CardDescription>Get personalized help with your learning</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/ai-tutor">Open AI Tutor →</Link>
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Database className="w-5 h-5 text-blue-500" />
                      SQL Playground
                    </CardTitle>
                    <CardDescription>Practice SQL queries with real databases</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/sql-playground">Open SQL Playground →</Link>
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Terminal className="w-5 h-5 text-emerald-500" />
                      Node.js Labs
                    </CardTitle>
                    <CardDescription>Run Node.js code directly in your browser</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/webcontainer-demo">Open Node.js Labs →</Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>

        {/* Quick Links */}
        <div className="mt-12 p-6 rounded-lg bg-muted/30 border">
          <h3 className="font-semibold mb-4">Quick Links</h3>
          <div className="flex flex-wrap gap-3">
            <Button variant="default" size="sm" asChild>
              <Link to="/my-courses">My Courses</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/courses">Browse Courses</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/search-courses">Search Courses</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/favorites">Favorites</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/learning-paths">Learning Paths</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/certificates">My Certificates</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/dashboard">Dashboard</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/notifications">Notifications</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/install">Install App</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings">Settings</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentHelp;
